<script lang="ts" setup>
import { onMounted, reactive, ref } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'
import BookGridCard from './BookGridCard.vue'
import { api } from '@/api'
import { useElementClick } from '@/composables/tracks/useElementClick'

const props = defineProps<{
  gotoBook: (id: string) => void
}>()

const { elementClick } = useElementClick()

const PAGE_SIZE = 20

type ClassItem = GetContentGetallclassResponse['data'][number]

const classes = ref<ClassItem[]>([])
const classLoading = ref(false)
const classError = ref<any>(null)
const activeClass = ref<ClassItem | null>(null)

const listState = reactive({
  items: [] as GetContentGetclasscontentlistResponse['data']['records'],
  page: 1,
  pages: 0,
  fetching: false,
  ending: false,
  error: null as any,
})

const listRef = ref<HTMLElement | null>(null)

async function fetchClasses() {
  classLoading.value = true
  classError.value = null
  try {
    const { data } = await api.get<any, GetContentGetallclassResponse>('/content/getAllClass')
    classes.value = data || []
    if (classes.value.length)
      selectClass(classes.value[0], false)
  }
  catch (e) {
    classError.value = e
  }
  finally {
    classLoading.value = false
  }
}

function resetList() {
  listState.items = []
  listState.page = 1
  listState.pages = 0
  listState.fetching = false
  listState.ending = false
  listState.error = null
}

function selectClass(item: ClassItem, track = true) {
  if (activeClass.value?.id === item.id)
    return
  if (track)
    elementClick(`category_${item.name}`)
  activeClass.value = item
  resetList()
  listRef.value?.scrollTo(0, 0)
  fetchBooks()
}

async function fetchBooks() {
  const current = activeClass.value
  if (!current || listState.fetching || listState.ending || listState.error)
    return
  listState.fetching = true
  try {
    const { data } = await api.get<any, GetContentGetclasscontentlistResponse>('/content/getClassContentList', {
      params: {
        contentChannel: current.contentChannel,
        contentClassId: current.id,
        pageIndex: listState.page,
        pageSize: PAGE_SIZE,
      },
    })
    // 切换分类后返回的旧请求丢弃
    if (activeClass.value?.id !== current.id)
      return
    listState.items = [...listState.items, ...(data.records || [])]
    listState.pages = data.pages || 0
    if (listState.page >= listState.pages)
      listState.ending = true
    else
      listState.page += 1
  }
  catch (e) {
    listState.error = e
  }
  finally {
    listState.fetching = false
  }
}

function retryBooks() {
  listState.error = null
  fetchBooks()
}

useInfiniteScroll(listRef, async () => {
  await fetchBooks()
}, { distance: 80 })

onMounted(() => {
  fetchClasses()
})
</script>

<template>
  <div class="category-panel min-h-0">
    <!-- 分类加载中：整面板居中 Loading -->
    <div v-if="classLoading" class="h-full flex items-center justify-center">
      <BasicSpinner />
    </div>

    <div v-else class="h-full flex min-h-0">
      <!-- 左栏：分类列表 -->
      <div class="class-col shrink-0 h-full overflow-y-auto no-scrollbar pb-90px">
        <div
          v-for="item in classes"
          :key="item.id"
          class="class-item relative flex items-center"
          :class="{ active: activeClass?.id === item.id }"
          @click="selectClass(item)"
        >
          <i v-if="activeClass?.id === item.id" class="class-indicator" />
          <span class="class-name">{{ item.name }}</span>
        </div>
      </div>

      <!-- 右栏：书籍双列网格 -->
      <div ref="listRef" class="flex-1 h-full overflow-y-auto no-scrollbar px-12px pb-90px">
        <div v-if="listState.error" @click="retryBooks">
          <BizBlockError />
        </div>
        <template v-else-if="listState.items.length">
          <div class="grid grid-cols-2 gap-x-10px gap-y-16px pt-12px">
            <BookGridCard
              v-for="book in listState.items"
              :key="book.id"
              :book="book"
              module-name="category"
              @click="props.gotoBook"
            />
          </div>
          <div v-if="listState.fetching" class="py-15px flex justify-center">
            <BasicSpinner />
          </div>
          <div v-if="listState.ending" class="py-15px text-12px text-n-text-2 text-center">
            - THE END -
          </div>
        </template>
        <!-- 首页加载中 -->
        <div v-else-if="listState.fetching" class="h-full flex items-center justify-center">
          <BasicSpinner />
        </div>
        <BizBlockEmpty v-else />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.no-scrollbar {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.class-col {
  width: 114px;
}

.class-item {
  min-height: 54px;
  padding: 10px 12px 10px 16px;
  cursor: pointer;

  .class-name {
    font-size: 14px;
    color: var(--vp-category-inactive, #7b7994);
    line-height: 18px;
    word-break: break-word;
  }

  &.active {
    background: linear-gradient(90deg, rgba(98, 52, 117, 1) 0%, rgba(44, 42, 87, 1) 100%);

    .class-name {
      color: #ffffff;
      font-weight: 600;
    }
  }
}

/* 选中态左侧指示条 */
.class-indicator {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 37px;
  border-radius: 0 20px 20px 0;
  background: linear-gradient(180deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
}
</style>
